import React, { useState, useCallback, useRef, useEffect } from 'react';
import { TOOLS } from '../utils/constants';

/**
 * Menu configuration - Rhino-style menu structure
 */
const MENU_CONFIG = [
  {
    id: 'file',
    label: 'Datei',
    items: [
      { id: 'save', label: 'Speichern', shortcut: 'Strg+S', action: 'save' },
      { id: 'export3dm', label: '3DM Export...', action: 'export3dm' },
      { id: 'importObj', label: 'OBJ Import...', action: 'importObj' },
      { separator: true },
      { id: 'reset', label: 'Zurücksetzen', action: 'reset' },
    ],
  },
  {
    id: 'edit',
    label: 'Bearbeiten',
    items: [
      { id: 'undo', label: 'Rückgängig', shortcut: 'Strg+Z', action: 'undo', disabled: true },
      { id: 'redo', label: 'Wiederholen', shortcut: 'Strg+Y', action: 'redo', disabled: true },
      { separator: true },
      { id: 'delete', label: 'Löschen', shortcut: 'Entf', action: 'delete' },
      { id: 'selectAll', label: 'Alles auswählen', shortcut: 'Strg+A', action: 'selectAll' },
      { id: 'deselectAll', label: 'Auswahl aufheben', shortcut: 'Esc', action: 'deselectAll' },
    ],
  },
  {
    id: 'view',
    label: 'Ansicht',
    items: [
      { id: 'view2d', label: '2D Grundriss', action: 'view2d' },
      { id: 'view3d', label: '3D Ansicht', action: 'view3d' },
      { separator: true },
      { id: 'zoomExtents', label: 'Zoom Extents', shortcut: 'Z', action: 'zoomExtents' },
      { id: 'zoomIn', label: 'Vergrößern', shortcut: '+', action: 'zoomIn' },
      { id: 'zoomOut', label: 'Verkleinern', shortcut: '-', action: 'zoomOut' },
      { separator: true },
      { id: 'toggleGrid', label: 'Raster ein/aus', shortcut: 'G', action: 'toggleGrid' },
      { id: 'toggleSnap', label: 'Fang ein/aus', shortcut: 'F', action: 'toggleSnap' },
    ],
  },
  {
    id: 'curves',
    label: 'Kurven',
    items: [
      { id: 'line', label: 'Linie', shortcut: 'L', tool: TOOLS.LINE },
      { id: 'polyline', label: 'Polylinie', shortcut: 'N', tool: TOOLS.POLYLINE },
      { separator: true },
      { id: 'arc', label: 'Bogen (3-Punkt)', shortcut: 'A', tool: TOOLS.ARC },
      { id: 'circle', label: 'Kreis', shortcut: 'K', tool: TOOLS.CIRCLE },
      { separator: true },
      { id: 'rectangle', label: 'Rechteck', shortcut: 'E', tool: TOOLS.RECTANGLE },
      { id: 'polygon', label: 'Polygon', shortcut: 'Y', tool: TOOLS.POLYGON },
      { separator: true },
      { id: 'construction', label: 'Hilfslinie', shortcut: 'H', tool: TOOLS.CONSTRUCTION_LINE },
    ],
  },
  {
    id: 'solids',
    label: 'Körper',
    items: [
      { id: 'box', label: 'Quader', shortcut: 'B', tool: TOOLS.BOX },
      { id: 'cylinder', label: 'Zylinder', shortcut: 'J', tool: TOOLS.CYLINDER },
      { id: 'sphere', label: 'Kugel', shortcut: 'U', tool: TOOLS.SPHERE },
      { separator: true },
      { id: 'extrude', label: 'Extrudieren...', tool: TOOLS.EXTRUDE, disabled: true },
    ],
  },
  {
    id: 'building',
    label: 'Bauteil',
    items: [
      { id: 'point', label: 'Punkt setzen', shortcut: 'P', tool: TOOLS.POINT },
      { id: 'wall', label: 'Wand', shortcut: 'W', tool: TOOLS.WALL },
      { id: 'space', label: 'Raum definieren', shortcut: 'R', tool: TOOLS.SPACE },
      { separator: true },
      { id: 'shaft', label: 'Schacht', shortcut: 'S', tool: TOOLS.SHAFT },
      { id: 'stubwall', label: 'Vorwand', shortcut: 'T', tool: TOOLS.STUB_WALL },
    ],
  },
  {
    id: 'transform',
    label: 'Transformieren',
    items: [
      { id: 'move', label: 'Verschieben', shortcut: 'M', action: 'transform.move' },
      { id: 'copy', label: 'Kopieren', shortcut: 'C', action: 'transform.copy' },
      { separator: true },
      { id: 'rotate', label: 'Drehen', action: 'transform.rotate' },
      { id: 'scale', label: 'Skalieren', action: 'transform.scale' },
      { id: 'mirror', label: 'Spiegeln', action: 'transform.mirror' },
      { separator: true },
      { id: 'arrayLinear', label: 'Lineares Array...', action: 'transform.arrayLinear', disabled: true },
      { id: 'arrayPolar', label: 'Polares Array...', action: 'transform.arrayPolar', disabled: true },
    ],
  },
  {
    id: 'tools',
    label: 'Extras',
    items: [
      { id: 'select', label: 'Auswählen', shortcut: 'V', tool: TOOLS.SELECT },
      { id: 'measure', label: 'Messen', shortcut: 'M', tool: TOOLS.MEASURE },
      { separator: true },
      { id: 'settings', label: 'Einstellungen...', action: 'settings' },
    ],
  },
];

/**
 * MenuBar - Rhino-style horizontal menu bar
 */
export function MenuBar({
  onSelectTool,
  onAction,
  activeTool,
}) {
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuBarRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuBarRef.current && !menuBarRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle menu item click
  const handleItemClick = useCallback((item) => {
    if (item.disabled) return;

    if (item.tool) {
      onSelectTool?.(item.tool);
    } else if (item.action) {
      onAction?.(item.action);
    }

    setOpenMenuId(null);
  }, [onSelectTool, onAction]);

  // Toggle menu open/close
  const toggleMenu = useCallback((menuId) => {
    setOpenMenuId(prev => prev === menuId ? null : menuId);
  }, []);

  // Handle mouse enter for menu switching
  const handleMenuEnter = useCallback((menuId) => {
    if (openMenuId !== null) {
      setOpenMenuId(menuId);
    }
  }, [openMenuId]);

  return (
    <div
      ref={menuBarRef}
      className="flex items-center bg-gray-100 border-b border-gray-300 h-8 select-none"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {MENU_CONFIG.map(menu => (
        <div key={menu.id} className="relative">
          {/* Menu Button */}
          <button
            className={`px-3 h-8 text-sm hover:bg-gray-200 transition-colors ${
              openMenuId === menu.id ? 'bg-gray-200' : ''
            }`}
            onClick={() => toggleMenu(menu.id)}
            onMouseEnter={() => handleMenuEnter(menu.id)}
          >
            {menu.label}
          </button>

          {/* Dropdown Menu */}
          {openMenuId === menu.id && (
            <div className="absolute left-0 top-8 bg-white border border-gray-300 shadow-lg rounded-b min-w-48 z-50">
              {menu.items.map((item, index) => {
                if (item.separator) {
                  return <div key={`sep-${index}`} className="border-t border-gray-200 my-1" />;
                }

                const isActive = item.tool && activeTool === item.tool;

                return (
                  <button
                    key={item.id}
                    className={`w-full px-3 py-1.5 text-left text-sm flex items-center justify-between transition-colors ${
                      item.disabled
                        ? 'text-gray-400 cursor-not-allowed'
                        : isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'hover:bg-gray-100'
                    }`}
                    onClick={() => handleItemClick(item)}
                    disabled={item.disabled}
                  >
                    <span>{item.label}</span>
                    {item.shortcut && (
                      <span className="text-xs text-gray-400 ml-4">{item.shortcut}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default MenuBar;
